import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import {
  civilFilters,
  civilSteps,
  criminalFilters,
  criminalSteps,
  type ProceduralFilter,
  type ProceduralPhase,
  type ProceduralPhaseKey,
} from '../data/proceduralPhases';
import type { CaseProceduralTrack } from '../types';

/**
 * Static reference timeline for Israeli civil/criminal procedure phases.
 * Two tracks (civil = תקסד"א, criminal = חוק סד"פ), each with phase
 * filters and click-to-expand details. The case's chosen track + current
 * phase are persisted on the Case row (proceduralTrack + proceduralPhase).
 * Phase content is Hebrew-only by design (statutory terms); chrome
 * strings are i18n'd. Hebrew data nodes get dir="rtl" inline so bullets
 * align right even when the surrounding UI is LTR (English).
 */
export function CaseProceduralPane({
  caseId,
  initialTrack,
  initialPhase,
  onUpdated,
}: {
  caseId: string;
  initialTrack: CaseProceduralTrack | null;
  initialPhase: number | null;
  onUpdated: () => void;
}) {
  const { t } = useTranslation(['cases', 'common']);
  // Local mirror of the case's track + phase. We persist optimistically
  // — the API call is fire-and-forget for UX speed, with a parent
  // refetch on success.
  const [track, setTrack] = useState<CaseProceduralTrack>(initialTrack ?? 'civil');
  const [currentPhase, setCurrentPhase] = useState<number | null>(initialPhase);
  const [filter, setFilter] = useState<ProceduralPhaseKey | 'all'>('all');
  const [openId, setOpenId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  // Re-sync if the parent reloads the case (e.g. another window saved a
  // change). Keep the local edit if the user is mid-flight.
  useEffect(() => {
    if (busy) return;
    setTrack(initialTrack ?? 'civil');
    setCurrentPhase(initialPhase);
  }, [initialTrack, initialPhase, busy]);

  const steps = track === 'civil' ? civilSteps : criminalSteps;
  const filters = track === 'civil' ? civilFilters : criminalFilters;
  const accent = track === 'civil' ? '#1A4A6E' : '#8B1A1A';

  const visibleSteps = useMemo(
    () => (filter === 'all' ? steps : steps.filter((s) => s.group === filter)),
    [steps, filter],
  );

  const currentPhaseLabel = useMemo(() => {
    if (currentPhase == null) return null;
    return steps.find((s) => s.id === currentPhase)?.title ?? null;
  }, [currentPhase, steps]);

  async function persist(next: { track?: CaseProceduralTrack; phase?: number | null }) {
    setBusy(true);
    try {
      await apiFetch(`/api/cases/${caseId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...(next.track !== undefined ? { proceduralTrack: next.track } : {}),
          ...(next.phase !== undefined ? { proceduralPhase: next.phase } : {}),
        }),
      });
      onUpdated();
    } catch {
      // Roll back local mirror on failure
      setTrack(initialTrack ?? 'civil');
      setCurrentPhase(initialPhase);
    } finally {
      setBusy(false);
    }
  }

  function switchTrack(next: CaseProceduralTrack) {
    if (next === track) return;
    setTrack(next);
    setFilter('all');
    setOpenId(null);
    // Switching tracks invalidates any previously-marked phase
    setCurrentPhase(null);
    void persist({ track: next, phase: null });
  }

  function toggleStep(id: number) {
    setOpenId((prev) => (prev === id ? null : id));
  }

  function markCurrent(id: number) {
    const next = currentPhase === id ? null : id;
    setCurrentPhase(next);
    void persist({ phase: next });
  }

  return (
    <div>
      {/* Track toggle (Civil / Criminal) */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: 16,
          background: 'var(--bg, #F2F0EB)',
          borderRadius: 7,
          padding: 3,
          width: 'fit-content',
          border: '0.5px solid var(--rule2)',
        }}
      >
        <button
          type="button"
          onClick={() => switchTrack('civil')}
          disabled={busy}
          style={{
            padding: '7px 18px',
            borderRadius: 5,
            fontSize: 12,
            fontWeight: 500,
            cursor: busy ? 'wait' : 'pointer',
            border: 'none',
            fontFamily: 'inherit',
            background: track === 'civil' ? '#1A4A6E' : 'transparent',
            color: track === 'civil' ? '#fff' : 'var(--text2)',
            transition: 'all .15s',
          }}
        >
          ⚖️ {t('detail.procedural.civil')}
        </button>
        <button
          type="button"
          onClick={() => switchTrack('criminal')}
          disabled={busy}
          style={{
            padding: '7px 18px',
            borderRadius: 5,
            fontSize: 12,
            fontWeight: 500,
            cursor: busy ? 'wait' : 'pointer',
            border: 'none',
            fontFamily: 'inherit',
            background: track === 'criminal' ? '#8B1A1A' : 'transparent',
            color: track === 'criminal' ? '#fff' : 'var(--text2)',
            transition: 'all .15s',
          }}
        >
          🔒 {t('detail.procedural.criminal')}
        </button>
      </div>

      {/* Stats — total phases / current phase / typical duration */}
      <ProceduralStats
        track={track}
        phaseCount={steps.length}
        currentPhase={currentPhase}
        currentPhaseLabel={currentPhaseLabel}
      />

      {/* Phase filters */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        {filters.map((f) => (
          <ProceduralFilterButton
            key={f.key}
            filter={f}
            active={filter === f.key}
            onClick={() => {
              setFilter(f.key);
              setOpenId(null);
            }}
          />
        ))}
      </div>

      {/* Phase list */}
      <div className="full-panel">
        <div
          className="panel-header"
          style={
            track === 'criminal'
              ? { background: 'linear-gradient(135deg, #FAE8E8, #FCEBEB)', color: '#8B1A1A' }
              : undefined
          }
        >
          <span>{t(`detail.procedural.title_${track}`)}</span>
          {currentPhase != null && (
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              {t('detail.procedural.mark_hint')}
            </span>
          )}
        </div>
        <div style={{ padding: 16, position: 'relative' }}>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              insetInlineEnd: 35,
              top: 16,
              bottom: 16,
              width: 1,
              background: 'var(--rule2)',
              zIndex: 0,
            }}
          />
          <div style={{ position: 'relative' }}>
            {visibleSteps.map((step) => (
              <ProceduralItem
                key={step.id}
                step={step}
                open={openId === step.id}
                isCurrent={currentPhase === step.id}
                accent={accent}
                onClick={() => toggleStep(step.id)}
                onMarkCurrent={() => markCurrent(step.id)}
              />
            ))}
            {visibleSteps.length === 0 && (
              <div className="proc-empty">{t('detail.procedural.empty')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProceduralFilterButton({
  filter,
  active,
  onClick,
}: {
  filter: ProceduralFilter;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 11px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 500,
        cursor: 'pointer',
        border: '0.5px solid ' + (active ? filter.color : 'var(--rule2)'),
        background: active ? filter.color : 'var(--surface, #fff)',
        color: active ? '#fff' : 'var(--text2)',
        fontFamily: 'inherit',
        transition: 'all .15s',
      }}
    >
      {filter.label}
    </button>
  );
}

function ProceduralStats({
  track,
  phaseCount,
  currentPhase,
  currentPhaseLabel,
}: {
  track: CaseProceduralTrack;
  phaseCount: number;
  currentPhase: number | null;
  currentPhaseLabel: string | null;
}) {
  const { t } = useTranslation(['cases']);
  const avg = track === 'civil' ? '12–18' : '18–36';
  return (
    <div className="metrics" style={{ marginBottom: 14, gridTemplateColumns: 'repeat(3, 1fr)' }}>
      <div className="metric">
        <div className="metric-label">{t('detail.procedural.stat_phases')}</div>
        <div className="metric-val">{phaseCount}</div>
        <div className="metric-sub">{t('detail.procedural.stat_phases_sub')}</div>
      </div>
      <div className="metric">
        <div className="metric-label">{t('detail.procedural.stat_current')}</div>
        <div className="metric-val">{currentPhase ?? '—'}</div>
        <div className="metric-sub" dir="auto">
          {currentPhaseLabel ?? t('detail.procedural.no_phase_marked')}
        </div>
      </div>
      <div className="metric">
        <div className="metric-label">{t('detail.procedural.stat_avg')}</div>
        <div className="metric-val">{avg}</div>
        <div className="metric-sub">{t('detail.procedural.stat_avg_sub')}</div>
      </div>
    </div>
  );
}

function ProceduralItem({
  step,
  open,
  isCurrent,
  accent,
  onClick,
  onMarkCurrent,
}: {
  step: ProceduralPhase;
  open: boolean;
  isCurrent: boolean;
  accent: string;
  onClick: () => void;
  onMarkCurrent: () => void;
}) {
  const { t } = useTranslation(['cases']);
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        gap: 12,
        paddingBottom: 16,
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 500,
          flexShrink: 0,
          background: step.bg,
          color: step.fg,
          border: '2px solid ' + (isCurrent ? accent : open ? step.border : 'transparent'),
          // Pulse the current phase visually
          boxShadow: isCurrent ? `0 0 0 3px ${accent}22` : 'none',
          position: 'relative',
          zIndex: 1,
          transition: 'transform .15s, border-color .15s, box-shadow .15s',
        }}
      >
        {step.id}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 2,
          }}
        >
          <div dir="auto" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', lineHeight: 1.3 }}>
            {step.title}
            {isCurrent && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  padding: '2px 7px',
                  borderRadius: 9,
                  background: accent,
                  color: '#fff',
                  marginInlineStart: 8,
                  verticalAlign: 'middle',
                }}
              >
                {t('detail.procedural.current_badge')}
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 20,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              marginTop: 2,
              background: step.deadlineBg,
              color: step.deadlineFg,
            }}
          >
            {step.deadline}
          </div>
        </div>
        <div dir="auto" style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 5 }}>
          {step.description}
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {step.tags.map((tag, i) => (
            <span
              key={i}
              style={{
                fontSize: 9,
                fontWeight: 600,
                padding: '2px 7px',
                borderRadius: 9,
                background: tag.bg,
                color: tag.fg,
              }}
            >
              {tag.label}
            </span>
          ))}
        </div>
        {open && (
          <div
            style={{
              background: 'var(--bg)',
              border: '0.5px solid var(--rule2)',
              borderRadius: 7,
              padding: '11px 13px',
              marginTop: 8,
              borderInlineStartWidth: 3,
              borderInlineStartColor: accent,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                marginBottom: 7,
              }}
            >
              {t('detail.procedural.required_actions')}
            </div>
            <ul dir="auto" style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4, margin: 0, padding: 0 }}>
              {step.details.map((detail, i) => (
                <li
                  key={i}
                  dir="auto"
                  style={{
                    fontSize: 11,
                    color: 'var(--text)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 6,
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: 'var(--text3)',
                      flexShrink: 0,
                      marginTop: 6,
                    }}
                  />
                  {detail}
                </li>
              ))}
            </ul>
            <div
              dir="auto"
              style={{
                fontSize: 10,
                color: 'var(--text3)',
                marginTop: 7,
                paddingTop: 7,
                borderTop: '0.5px solid var(--rule)',
              }}
            >
              {step.ref}
            </div>
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkCurrent();
                }}
                style={{
                  padding: '5px 11px',
                  borderRadius: 7,
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: 'pointer',
                  border: '0.5px solid ' + accent,
                  background: isCurrent ? accent : 'var(--surface, #fff)',
                  color: isCurrent ? '#fff' : accent,
                  fontFamily: 'inherit',
                  transition: 'all .15s',
                }}
              >
                {isCurrent
                  ? t('detail.procedural.unmark_current')
                  : t('detail.procedural.mark_current')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
